import { useLazyQuery } from "@apollo/client";
import { useCallback } from "react";

import {
  FETCH_BROCHURE_PORTRAIT_VIEW_URL,
  GET_BROCHURE_PORTRAIT_UPLOAD_URL,
} from "../graphql/query/BrochurePortrait";

/**
 * Upload one artist portrait straight from the browser to S3.
 *
 * The server signs the file's exact size into the upload URL, so it refuses to
 * issue one for anything over 1MB and S3 turns away a PUT of any other length.
 * What comes back is the handle to save against the artist and a short-lived URL
 * to show the portrait from.
 */
const useBrochurePortraitUpload = () => {
  const [getUploadUrl, { loading: uploading }] = useLazyQuery(
    GET_BROCHURE_PORTRAIT_UPLOAD_URL,
    { fetchPolicy: "network-only" },
  );
  const [getViewUrl] = useLazyQuery(FETCH_BROCHURE_PORTRAIT_VIEW_URL, {
    fetchPolicy: "network-only",
  });

  const uploadPortrait = useCallback(
    async file => {
      const { data } = await getUploadUrl({
        variables: {
          input: { filename: file.name, contentLength: file.size },
        },
      });
      const { success, uploadUrl, handle, viewUrl, error } =
        data?.getBrochurePortraitUploadUrl ?? {};

      if (!success || !uploadUrl) {
        return {
          success: false,
          error: error || "Could not start the upload.",
        };
      }

      const response = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!response.ok) {
        return { success: false, error: "The upload was refused." };
      }

      return { success: true, handle, viewUrl };
    },
    [getUploadUrl],
  );

  const fetchPortraitViewUrl = useCallback(
    async handle => {
      const { data } = await getViewUrl({ variables: { handle } });
      return data?.brochurePortraitViewUrl?.viewUrl ?? null;
    },
    [getViewUrl],
  );

  return { uploadPortrait, fetchPortraitViewUrl, uploading };
};

export default useBrochurePortraitUpload;
